/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  SignalIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import {
  driftService,
  monitoringService,
  DriftMonitor,
  DriftReport,
} from '../../services/registryApi';
import toast from 'react-hot-toast';

/* ── helpers ─────────────────────────────────────────────────────────── */

const methodLabels: Record<string, string> = {
  psi: 'PSI',
  ks_test: 'KS Test',
  js_divergence: 'JS Divergence',
};

type DriftStatus = 'healthy' | 'warning' | 'drift';

const deriveDriftStatus = (
  latestReport: DriftReport | null,
  threshold: number,
): DriftStatus => {
  if (!latestReport) return 'healthy';
  if (latestReport.drift_detected) return 'drift';
  if (latestReport.overall_score > threshold * 0.7) return 'warning';
  return 'healthy';
};

const statusConfig: Record<
  DriftStatus,
  { label: string; bg: string; text: string; dot: string }
> = {
  healthy: {
    label: 'Healthy',
    bg: 'bg-green-500/20',
    text: 'text-green-400',
    dot: 'bg-green-400',
  },
  warning: {
    label: 'Warning',
    bg: 'bg-yellow-500/20',
    text: 'text-yellow-400',
    dot: 'bg-yellow-400',
  },
  drift: {
    label: 'Drift Detected',
    bg: 'bg-red-500/20',
    text: 'text-red-400',
    dot: 'bg-red-400',
  },
};

/* ── component ───────────────────────────────────────────────────────── */

const DriftDashboard: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /* ---- create modal state ---- */
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    monitor_id: '',
    name: '',
    features: '',
    method: 'psi',
    threshold: 0.2,
    check_schedule: '0 */6 * * *',
    sample_size: 1000,
    auto_retrain: false,
    pipeline_id: '',
  });

  /* ---- queries ---- */
  const { data: driftData, isLoading } = useQuery({
    queryKey: ['drift-monitors'],
    queryFn: () => driftService.list({ limit: 100 }).then((r) => r.data),
  });

  const monitors: DriftMonitor[] = driftData?.drift_monitors || [];

  // Fetch latest report for each monitor so we can show status
  const { data: monitorDetails } = useQuery({
    queryKey: ['drift-monitor-details', monitors.map((m) => m.id)],
    queryFn: async () => {
      if (monitors.length === 0) return {};
      const results: Record<string, DriftReport | null> = {};
      await Promise.all(
        monitors.map(async (m) => {
          try {
            const res = await driftService.get(m.id);
            results[m.id] = res.data.latest_report;
          } catch {
            results[m.id] = null;
          }
        }),
      );
      return results;
    },
    enabled: monitors.length > 0,
  });

  // Fetch model monitors for the create form dropdown
  const { data: modelMonitorsData } = useQuery({
    queryKey: ['model-monitors-for-drift'],
    queryFn: () => monitoringService.list({ limit: 200 }).then((r) => r.data),
    enabled: showCreate,
  });

  const modelMonitors = modelMonitorsData?.monitors || [];

  /* ---- mutations ---- */
  const createMutation = useMutation({
    mutationFn: () => {
      const features = form.features
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean);
      return driftService.create({
        monitor_id: form.monitor_id,
        name: form.name,
        features: features.length > 0 ? features : undefined,
        method: form.method,
        threshold: form.threshold,
        check_schedule: form.check_schedule,
        sample_size: form.sample_size,
        auto_retrain: form.auto_retrain,
        pipeline_id: form.auto_retrain ? form.pipeline_id || undefined : undefined,
      });
    },
    onSuccess: () => {
      toast.success('Drift monitor created');
      queryClient.invalidateQueries({ queryKey: ['drift-monitors'] });
      setShowCreate(false);
      setForm({
        monitor_id: '',
        name: '',
        features: '',
        method: 'psi',
        threshold: 0.2,
        check_schedule: '0 */6 * * *',
        sample_size: 1000,
        auto_retrain: false,
        pipeline_id: '',
      });
    },
    onError: () => {
      toast.error('Failed to create drift monitor');
    },
  });

  const triggerCheckMutation = useMutation({
    mutationFn: (id: string) => driftService.triggerCheck(id),
    onSuccess: () => {
      toast.success('Drift check triggered');
      queryClient.invalidateQueries({ queryKey: ['drift-monitors'] });
      queryClient.invalidateQueries({ queryKey: ['drift-monitor-details'] });
    },
    onError: () => {
      toast.error('Failed to trigger drift check');
    },
  });

  /* ---- derived ---- */
  const statusCounts = useMemo(() => {
    const counts = { healthy: 0, warning: 0, drift: 0 };
    monitors.forEach((m) => {
      const report = monitorDetails?.[m.id] ?? null;
      const status = deriveDriftStatus(report, m.threshold);
      counts[status]++;
    });
    return counts;
  }, [monitors, monitorDetails]);

  /* ---- render ---- */
  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3">
              <SignalIcon className="w-8 h-8 text-orange-500" />
              <h1 className="text-2xl font-bold text-white">Drift Detection</h1>
            </div>
            <p className="text-gray-400 mt-1">
              Monitor feature and concept drift across your deployed models.
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Create Drift Monitor
          </button>
        </div>

        {/* Status Overview */}
        {monitors.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/20">
                <CheckCircleIcon className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Healthy</p>
                <p className="text-2xl font-bold text-white">{statusCounts.healthy}</p>
              </div>
            </div>
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-yellow-500/20">
                <ExclamationTriangleIcon className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Warning</p>
                <p className="text-2xl font-bold text-white">{statusCounts.warning}</p>
              </div>
            </div>
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-red-500/20">
                <XCircleIcon className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Drift Detected</p>
                <p className="text-2xl font-bold text-white">{statusCounts.drift}</p>
              </div>
            </div>
          </div>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-lg mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold text-white mb-6">
                Create Drift Monitor
              </h2>
              <div className="space-y-4">
                {/* Monitor ID */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Linked Model Monitor *
                  </label>
                  <select
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    value={form.monitor_id}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, monitor_id: e.target.value }))
                    }
                  >
                    <option value="">Select a model monitor...</option>
                    {modelMonitors.map((mm) => (
                      <option key={mm.id} value={mm.id}>
                        {mm.name} ({mm.id.slice(0, 8)})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Name *</label>
                  <input
                    type="text"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="feature-drift-monitor"
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                </div>

                {/* Features */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Features (comma-separated)
                  </label>
                  <input
                    type="text"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="age, income, credit_score"
                    value={form.features}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, features: e.target.value }))
                    }
                  />
                </div>

                {/* Method & Threshold */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Method</label>
                    <select
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                      value={form.method}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, method: e.target.value }))
                      }
                    >
                      <option value="psi">PSI</option>
                      <option value="ks_test">KS Test</option>
                      <option value="js_divergence">JS Divergence</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">
                      Threshold
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                      value={form.threshold}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          threshold: parseFloat(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                </div>

                {/* Schedule & Sample Size */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">
                      Check Schedule (cron)
                    </label>
                    <input
                      type="text"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="0 */6 * * *"
                      value={form.check_schedule}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, check_schedule: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">
                      Sample Size
                    </label>
                    <input
                      type="number"
                      min="1"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                      value={form.sample_size}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          sample_size: parseInt(e.target.value, 10) || 1000,
                        }))
                      }
                    />
                  </div>
                </div>

                {/* Auto Retrain Toggle */}
                <div className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg px-4 py-3">
                  <div>
                    <p className="text-sm text-white font-medium">Auto Retrain</p>
                    <p className="text-xs text-gray-400">
                      Trigger a pipeline when drift is detected
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setForm((f) => ({ ...f, auto_retrain: !f.auto_retrain }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      form.auto_retrain ? 'bg-orange-500' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        form.auto_retrain ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Pipeline ID (conditional) */}
                {form.auto_retrain && (
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">
                      Pipeline ID
                    </label>
                    <input
                      type="text"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="pipeline-uuid"
                      value={form.pipeline_id}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, pipeline_id: e.target.value }))
                      }
                    />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => createMutation.mutate()}
                  disabled={
                    createMutation.isPending || !form.name || !form.monitor_id
                  }
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Monitor'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Monitor Grid */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          </div>
        ) : monitors.length === 0 ? (
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-16 text-center">
            <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-600 mb-3" />
            <h3 className="text-sm font-medium text-white mb-1">
              No drift monitors yet
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Create a drift monitor to start tracking data and concept drift on
              your production models.
            </p>
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Create Drift Monitor
            </button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {monitors.map((monitor) => {
              const latestReport = monitorDetails?.[monitor.id] ?? null;
              const driftStatus = deriveDriftStatus(
                latestReport,
                monitor.threshold,
              );
              const cfg = statusConfig[driftStatus];

              return (
                <div
                  key={monitor.id}
                  className="bg-gray-900 rounded-xl border border-gray-800 p-5 hover:border-orange-500/50 transition-all"
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-white truncate">
                        {monitor.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {methodLabels[monitor.method] || monitor.method} &middot;
                        threshold {monitor.threshold}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded ${cfg.bg} ${cfg.text}`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}
                      />
                      {cfg.label}
                    </span>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-800/60 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Overall Score</p>
                      <p className="text-lg font-bold text-white">
                        {latestReport
                          ? latestReport.overall_score.toFixed(4)
                          : '--'}
                      </p>
                    </div>
                    <div className="bg-gray-800/60 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Last Check</p>
                      <p className="text-sm font-medium text-white">
                        {monitor.last_check_at
                          ? new Date(monitor.last_check_at).toLocaleDateString()
                          : 'Never'}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-3 border-t border-gray-800">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        triggerCheckMutation.mutate(monitor.id);
                      }}
                      disabled={triggerCheckMutation.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-400 bg-orange-500/10 hover:bg-orange-500/20 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <ArrowPathIcon className="w-3.5 h-3.5" />
                      Run Check
                    </button>
                    <button
                      onClick={() =>
                        navigate(`/registry/drift/${monitor.id}`)
                      }
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <ChartBarIcon className="w-3.5 h-3.5" />
                      View Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriftDashboard;
